import { useTranslation } from 'react-i18next';
import type { Competition } from '../api.js';
import CompetitionFlag from './CompetitionFlag.js';
import styles from './CompetitionPicker.module.css';

/**
 * Which competition to build a bolão for — a scrollable list of buttons, one
 * per competition, each showing its flag beside its name.
 *
 * Deliberately not a native `<select>`: a `<select>`'s `<option>`s can only
 * ever render plain text, so a flag there has to be an emoji baked into the
 * name — and Windows' native `<select>`/`<option>` control can't render
 * multi-codepoint flag emoji at all (it falls back to the raw two-letter
 * region code). This list uses `CompetitionFlag`'s flagcdn.com image
 * instead, so every row's flag renders correctly everywhere, not just on
 * platforms whose OS font happens to support it.
 */
export default function CompetitionPicker({
  competitions,
  value,
  onChange,
}: {
  competitions: readonly Competition[];
  value: string;
  onChange: (competitionId: string) => void;
}) {
  const { t } = useTranslation();

  if (competitions.length === 0) {
    return <p className="muted">{t('common.loading')}</p>;
  }

  return (
    <div
      className={styles.list}
      role="radiogroup"
      aria-label={t('createCompetition.competitionLabel')}
    >
      {competitions.map((competition) => {
        const selected = competition.id === value;
        return (
          <button
            key={competition.id}
            type="button"
            role="radio"
            aria-checked={selected}
            className={`${styles.row} ${selected ? styles.rowSelected : ''}`}
            onClick={() => onChange(competition.id)}
          >
            <CompetitionFlag slug={competition.slug} />
            {competition.displayName}
          </button>
        );
      })}
    </div>
  );
}
